import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, CheckCircle2, Phone, Star, Quote } from 'lucide-react';
import { COMPANY, SERVICES, FEATURES, STEPS, TESTIMONIALS, AREAS, VAN_PRICING, HERO_IMAGE, ABOUT_IMAGE } from '../mock';
import QuoteForm from '../components/QuoteForm';
import EstimateWizard from '../components/EstimateWizard';
import LicensesSection from '../components/LicensesSection';
import { OrgCarousel } from '../components/OrgCarousel';
import { ReviewsCarousel, GOOGLE_READ_URL } from '../components/ReviewsCarousel';
import { FlyTippingTeaser } from './FlyTipping';
import { HeroOffer } from '../components/HeroOffer';

const Home = () => {
  return (
    <>
      <OrgCarousel />

      {/* HERO */}
      <section className="hero-bg hero-grain relative text-white">
        <div className="container-xl relative py-8 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 xl:col-span-6 fade-up">
            <HeroOffer />
          </div>

          <div id="quote" className="lg:col-span-5 xl:col-span-6 fade-up scroll-mt-24" style={{ animationDelay: '120ms' }}>
            <EstimateWizard />
          </div>
        </div>

        {/* Trust strip */}
        <div className="relative border-t border-white/10 bg-black/20 backdrop-blur">
          <div className="container-xl py-5 flex flex-wrap items-center justify-between gap-6 text-sm">
            <a href={GOOGLE_READ_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group" aria-label="Five stars. Read our verified reviews on Google (opens in a new tab)" data-testid="hero-reviews-link">
              <span className="flex items-center gap-0.5" aria-hidden="true">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[color:var(--mv-yellow)] text-[color:var(--mv-yellow)]" />)}</span>
              <span className="text-white/90 font-semibold ml-1 underline underline-offset-2 group-hover:text-white">Reviews on Google</span>
            </a>
            <div className="flex items-center gap-6 text-white/70">
              <span>✔ Registered waste carrier</span>
              <span>✔ Insured — ask us for cover details</span>
            </div>
          </div>
        </div>
      </section>

      <div id="reviews" className="scroll-mt-24"><ReviewsCarousel /></div>

      {/* SERVICES */}
      <section className="section">
        <div className="container-xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="text-[color:var(--mv-orange)] font-bold tracking-[0.2em] text-xs uppercase mb-3">What we do</div>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--mv-navy)]">A full-service removals &amp; clearance team</h2>
              <p className="text-slate-600 mt-4 text-lg">From a single sofa to a 5-bed home — we have the right van, the right crew and the right price for every job.</p>
            </div>
            <Link to="/services" className="btn-ghost-dark self-start">View all services <ArrowRight className="w-4 h-4" /></Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.Truck;
              return (
                <article key={s.id} className="service-card group" style={{ animationDelay: `${i * 40}ms` }}>
                  <div className="overflow-hidden">
                    <img src={s.image} alt={s.title} className="thumb" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <div className="w-11 h-11 -mt-11 relative mb-3 rounded-md bg-[color:var(--mv-orange)] text-white flex items-center justify-center shadow-lg">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-display text-xl font-extrabold text-[color:var(--mv-navy)]">{s.title}</h3>
                    <p className="text-slate-500 text-sm mt-1">{s.short}</p>
                    <p className="text-slate-700 mt-3 leading-relaxed">{s.description}</p>
                    <Link to="/services" className="mt-4 inline-flex items-center gap-1.5 text-[color:var(--mv-orange)] font-semibold hover:gap-2.5 transition-all">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT STRIP */}
      <section className="relative bg-[color:var(--mv-sky)]">
        <div className="container-xl py-20 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src={ABOUT_IMAGE} alt="Move Van team" className="rounded-xl w-full aspect-[4/3] object-cover shadow-xl" />
            <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-5 w-60 hidden md:block border border-slate-100">
              <div className="text-[color:var(--mv-orange)] font-display text-4xl font-extrabold leading-none">18 vans</div>
              <div className="text-sm text-slate-600 mt-1">Our own fleet, based in London</div>
            </div>
          </div>
          <div>
            <div className="text-[color:var(--mv-orange)] font-bold tracking-[0.2em] text-xs uppercase mb-3">About Move Van</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--mv-navy)] leading-tight">A local team you can actually count on.</h2>
            <p className="text-slate-700 mt-5 text-lg leading-relaxed">
              Move Van Ltd is an independent, family-run removals and clearance company based in East London. We help families, students, landlords and businesses with house moves, deliveries and clearances.
            </p>
            <div className="mt-7 grid grid-cols-2 gap-5">
              {FEATURES.slice(0, 4).map((f) => {
                const Icon = Icons[f.icon] || Icons.CheckCircle2;
                return (
                  <div key={f.title} className="flex gap-3">
                    <div className="w-10 h-10 shrink-0 rounded-md bg-white border border-slate-200 flex items-center justify-center text-[color:var(--mv-orange)]">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-bold text-[color:var(--mv-navy)]">{f.title}</div>
                      <div className="text-sm text-slate-600">{f.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 flex gap-4">
              <Link to="/about" className="btn-primary">More about us <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/#quote" className="btn-ghost-dark">Get a quote</Link>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section">
        <div className="container-xl">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[color:var(--mv-orange)] font-bold tracking-[0.2em] text-xs uppercase mb-3">How it works</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--mv-navy)]">Booking a move takes 60 seconds</h2>
            <p className="text-slate-600 mt-4">Four simple steps and we’re on our way to your door.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.n} className="relative bg-white rounded-xl border border-slate-100 p-7 hover:border-[color:var(--mv-orange)] transition-colors group">
                <div className="font-display text-6xl font-extrabold text-slate-100 group-hover:text-orange-100 transition-colors">{s.n}</div>
                <h3 className="font-display text-xl font-extrabold text-[color:var(--mv-navy)] mt-2">{s.title}</h3>
                <p className="text-slate-600 mt-2">{s.text}</p>
                {i < STEPS.length - 1 && (
                  <ArrowRight className="w-6 h-6 text-slate-300 absolute -right-3 top-10 hidden lg:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="bg-[color:var(--mv-navy)] relative overflow-hidden">
        <div className="absolute -left-20 -bottom-20 w-96 h-96 rounded-full bg-[color:var(--mv-orange)]/15 blur-3xl" />
        <div className="container-xl section relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 text-white">
            <div className="max-w-xl">
              <div className="text-[color:var(--mv-orange-2)] font-bold tracking-[0.2em] text-xs uppercase mb-3">Transparent pricing</div>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold">Fixed hourly rates. No surprises.</h2>
              <p className="text-white/70 mt-4">Pick the van and crew that suit your job. 2-hour minimum (4 hours for the 7.5 tonne lorry); mileage applies outside our base London area.</p>
            </div>
            <Link to="/prices" className="btn-primary self-start">See all prices <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VAN_PRICING.map((p) => (
              <div key={p.size} className={`price-card ${p.featured ? 'featured' : ''}`}>
                {p.featured && <div className="inline-block mb-3 px-3 py-1 bg-[color:var(--mv-orange)] text-white rounded-full text-xs font-bold tracking-wider uppercase">Most Popular</div>}
                <div className="text-slate-500 font-semibold">{p.size}</div>
                <div className="flex items-end gap-1 mt-2">
                  <span className="font-display text-5xl font-extrabold text-[color:var(--mv-navy)]">{p.price}</span>
                  <span className="text-slate-500 pb-2">{p.unit.startsWith('per ') ? '/ ' + p.unit.slice(4) : p.unit}</span>
                </div>
                <div className="text-sm text-slate-500 mt-1">{p.capacity} · {p.best}</div>
                <ul className="mt-4 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-[color:var(--mv-orange)]" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/#quote" className={`mt-5 w-full justify-center ${p.featured ? 'btn-primary' : 'btn-ghost-dark'}`}>Book this van</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LICENSES */}
      <LicensesSection />


      {/* AREAS */}      <section className="section bg-[color:var(--mv-sky)]">
        <div className="container-xl">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="text-[color:var(--mv-orange)] font-bold tracking-[0.2em] text-xs uppercase mb-3">Service areas</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--mv-navy)]">Removals and clearances in London and nearby towns</h2>
            <p className="text-slate-600 mt-4">
              We carry out house removals, man and van jobs, furniture deliveries and clearances across Greater London and the towns around it. Moving between two of the places below, or somewhere close by? Pop your postcodes into the instant quote to check we cover your route.
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5 justify-center">
            {AREAS.map((a) => (
              <span key={a} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium text-[color:var(--mv-navy)] hover:bg-[color:var(--mv-orange)] hover:text-white hover:border-[color:var(--mv-orange)] transition-colors" data-testid={`area-${a.toLowerCase().replace(/\s/g, '-')}`}>
                {a}
              </span>
            ))}
            <span className="px-4 py-2 rounded-full bg-[color:var(--mv-navy)] text-white text-sm font-semibold">
              + towns in between
            </span>
          </div>
        </div>
      </section>

      <FlyTippingTeaser />

      {/* CTA */}
      <section className="section">
        <div className="container-xl">
          <div className="relative overflow-hidden rounded-2xl bg-[color:var(--mv-navy)] p-10 md:p-16 text-white">
            <div className="absolute -right-16 -top-16 w-80 h-80 rounded-full bg-[color:var(--mv-orange)]/25 blur-3xl" />
            <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url(${HERO_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-display text-4xl md:text-5xl font-extrabold leading-tight">Ready to move? Let’s make it simple.</h2>
                <p className="text-white/80 mt-4 text-lg">Call us now or request a free, no-obligation quote in under a minute.</p>
              </div>
              <div className="flex flex-wrap gap-4 lg:justify-end">
                <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="btn-primary"><Phone className="w-4 h-4" /> Call {COMPANY.phone}</a>
                <Link to="/#quote" className="btn-outline">Get a Free Quote</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
